<form (submit)="$event.preventDefault(); onSubmit()" class="flex flex-col gap-5">
  <fieldset class="border-0 p-0 m-0 flex flex-col gap-5" [disabled]="allDisable() || isLoading()">
    <!-- ======================================================== -->
    <!-- CAS 1 : ÉTAPE D'AJUSTEMENT POST-VISITE (OFFRE FINALE)     -->
    <!-- L'avis de valeur & repère DVF arrivent EN PREMIER          -->
    <!-- Le taux d'honoraires arrive APRÈS                          -->
    <!-- ======================================================== -->
    @if (isFinalOfferAdjustment()) {
      <!-- 1. AVIS DE VALEUR & REPÈRE MARCHÉ DVF -->
      <div class="border border-neutral-3 rounded-2xl p-4 sm:p-5 flex flex-col gap-4 bg-neutral-1/50">
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <span class="text-xs font-extrabold text-neutral-10 uppercase tracking-wider block">
              Avis de valeur du bien
            </span>
            @if (surfaceHabitable(); as surf) {
              <span
                class="text-[11px] font-semibold text-neutral-11 bg-neutral-2 border border-neutral-3 px-2 py-0.5 rounded-md"
              >
                Surface : {{ surf }} m²
              </span>
            }
          </div>
          <span class="text-xs text-neutral-11">
            Déterminez vos 3 niveaux d'estimation de prix suite à votre visite physique du bien en vous
            appuyant sur les repères de marché officiels ci-dessous.
          </span>
        </div>

        <!-- 3 Niveaux d'estimation -->
        <div class="flex flex-col gap-2 pt-1">
          <span class="text-xs font-bold text-neutral-12"> Vos 3 niveaux d'estimation post-visite </span>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <!-- 1. Prix attractif (Obligatoire en offre finale, inférieur au prix conseillé) -->
            <div
              class="flex flex-col gap-1.5 p-3 rounded-xl border border-neutral-3 bg-white dark:bg-neutral-2"
            >
              <div class="flex items-center justify-between">
                <label for="application-attractivePrice" class="text-xs font-bold text-neutral-12">
                  Prix attractif *
                </label>
                <div class="flex items-center gap-1.5">
                  @if (attractivePricePercentDiff(); as diff) {
                    <span
                      class="text-[10px] font-bold px-1.5 py-0.5 rounded-md border"
                      [class.bg-teal-3]="diff < 0"
                      [class.text-teal-12]="diff < 0"
                      [class.border-teal-5]="diff < 0"
                      [class.bg-red-3]="diff >= 0"
                      [class.text-red-12]="diff >= 0"
                      [class.border-red-5]="diff >= 0"
                    >
                      {{ diff > 0 ? '+' : '' }}{{ diff }} %
                    </span>
                  }
                  <span class="text-[10px] font-bold text-teal-11 bg-teal-a2 px-2 py-0.5 rounded-full">
                    Vente rapide
                  </span>
                </div>
              </div>
              <div class="relative flex items-center">
                <input
                  id="application-attractivePrice"
                  type="number"
                  step="1000"
                  [formField]="formTree.attractivePrice"
                  (keydown)="onPriceKeyDown('attractivePrice', $event)"
                  placeholder="Requis"
                  class="w-full rounded-lg border border-neutral-3 bg-neutral-1 p-2 pr-7 text-xs font-bold text-neutral-12 focus:border-primary focus:outline-none disabled:opacity-60 transition-colors"
                  [class.!border-red-7]="isAttractivePriceInvalid()"
                  [class.!bg-red-1/40]="isAttractivePriceInvalid()"
                />
                <span class="absolute right-2.5 text-xs font-bold text-neutral-9">€</span>
              </div>
              @if (isAttractivePriceInvalid()) {
                <span class="text-red-11 text-[10px] font-semibold flex items-center gap-1">
                  <mat-icon class="!text-[11px] size-3 text-red-10">error</mat-icon>
                  <span>{{ attractivePriceErrorMessage() }}</span>
                </span>
              } @else {
                <div class="flex items-center justify-between text-[10px]">
                  <span class="text-neutral-10">
                    @if (attractivePricePercentDiff(); as diff) {
                      {{ diff }} % vs conseillé
                    } @else {
                      Idéal pour déclencher des offres
                    }
                  </span>
                  @if (attractivePriceM2(); as m2) {
                    <span class="font-bold text-neutral-11">~{{ m2 | number: '1.0-0' : 'fr-FR' }} €/m²</span>
                  }
                </div>
              }
            </div>

            <!-- 2. Prix de vente conseillé (Champ central recommandé) -->
            <div
              class="flex flex-col gap-1.5 p-3 rounded-xl border-2 border-primary/60 bg-primary-surface/30"
            >
              <div class="flex items-center justify-between">
                <label for="application-recommendedPrice" class="text-xs font-extrabold text-primary">
                  Prix conseillé *
                </label>
                <div class="flex items-center gap-1.5">
                  @if (recommendedPriceVsOwnerPercentDiff(); as diff) {
                    <span
                      class="text-[10px] font-bold px-1.5 py-0.5 rounded-md border"
                      [class.bg-jade-3]="diff === 0"
                      [class.text-jade-12]="diff === 0"
                      [class.border-jade-5]="diff === 0"
                      [class.bg-amber-3]="diff < 0"
                      [class.text-amber-12]="diff < 0"
                      [class.border-amber-5]="diff < 0"
                      [class.bg-indigo-3]="diff > 0"
                      [class.text-indigo-12]="diff > 0"
                      [class.border-indigo-5]="diff > 0"
                    >
                      {{ diff > 0 ? '+' : '' }}{{ diff }} % vs vendeur
                    </span>
                  } @else if (sellingPriceEuros(); as ownerPrice) {
                    <span
                      class="text-[10px] font-medium text-neutral-11 bg-neutral-2 px-1.5 py-0.5 rounded-md border border-neutral-3"
                    >
                      Vendeur : {{ ownerPrice | number: '1.0-0' : 'fr-FR' }} €
                    </span>
                  }
                  <span class="text-[10px] font-extrabold text-white bg-primary px-2 py-0.5 rounded-full">
                    Recommandé
                  </span>
                </div>
              </div>
              <div class="relative flex items-center">
                <input
                  id="application-recommendedPrice"
                  type="number"
                  step="1000"
                  [formField]="formTree.recommendedPrice"
                  (keydown)="onPriceKeyDown('recommendedPrice', $event)"
                  placeholder="Requis"
                  class="w-full rounded-lg border border-primary/40 bg-white dark:bg-neutral-2 p-2 pr-7 text-xs font-bold text-neutral-12 focus:border-primary focus:outline-none disabled:opacity-60"
                />
                <span class="absolute right-2.5 text-xs font-bold text-neutral-9">€</span>
              </div>
              <div class="flex items-center justify-between text-[10px]">
                <span class="text-neutral-11 font-medium">
                  @if (sellingPriceEuros(); as ownerPrice) {
                    @if (recommendedPriceVsOwnerPercentDiff(); as diff) {
                      {{ diff > 0 ? '+' : '' }}{{ diff }} % vs prix vendeur ({{
                        ownerPrice | number: '1.0-0' : 'fr-FR'
                      }}
                      €)
                    } @else {
                      Prix vendeur : {{ ownerPrice | number: '1.0-0' : 'fr-FR' }} €
                    }
                  } @else {
                    Juste prix de marché cible
                  }
                </span>
                @if (recommendedPriceM2(); as m2) {
                  <span class="font-bold text-primary">~{{ m2 | number: '1.0-0' : 'fr-FR' }} €/m²</span>
                }
              </div>
            </div>

            <!-- 3. Prix coup de cœur (Obligatoire en offre finale, supérieur au prix conseillé) -->
            <div
              class="flex flex-col gap-1.5 p-3 rounded-xl border border-neutral-3 bg-white dark:bg-neutral-2"
            >
              <div class="flex items-center justify-between">
                <label for="application-heartStrokePrice" class="text-xs font-bold text-neutral-12">
                  Prix coup de cœur *
                </label>
                <div class="flex items-center gap-1.5">
                  @if (heartStrokePricePercentDiff(); as diff) {
                    <span
                      class="text-[10px] font-bold px-1.5 py-0.5 rounded-md border"
                      [class.bg-amber-3]="diff > 0"
                      [class.text-amber-12]="diff > 0"
                      [class.border-amber-5]="diff > 0"
                      [class.bg-red-3]="diff <= 0"
                      [class.text-red-12]="diff <= 0"
                      [class.border-red-5]="diff <= 0"
                    >
                      {{ diff > 0 ? '+' : '' }}{{ diff }} %
                    </span>
                  }
                  <span class="text-[10px] font-bold text-amber-11 bg-amber-a2 px-2 py-0.5 rounded-full">
                    Fourchette haute
                  </span>
                </div>
              </div>
              <div class="relative flex items-center">
                <input
                  id="application-heartStrokePrice"
                  type="number"
                  step="1000"
                  [formField]="formTree.heartStrokePrice"
                  (keydown)="onPriceKeyDown('heartStrokePrice', $event)"
                  placeholder="Requis"
                  class="w-full rounded-lg border border-neutral-3 bg-white dark:bg-neutral-2 p-2 pr-7 text-xs font-bold text-neutral-12 focus:border-primary focus:outline-none disabled:opacity-60 transition-colors"
                  [class.!border-red-7]="isHeartStrokePriceInvalid()"
                  [class.!bg-red-1/40]="isHeartStrokePriceInvalid()"
                />
                <span class="absolute right-2.5 text-xs font-bold text-neutral-9">€</span>
              </div>
              @if (isHeartStrokePriceInvalid()) {
                <span class="text-red-11 text-[10px] font-semibold flex items-center gap-1">
                  <mat-icon class="!text-[11px] size-3 text-red-10">error</mat-icon>
                  <span>{{ heartStrokePriceErrorMessage() }}</span>
                </span>
              } @else {
                <div class="flex items-center justify-between text-[10px]">
                  <span class="text-neutral-10">
                    @if (heartStrokePricePercentDiff(); as diff) {
                      {{ diff > 0 ? '+' : '' }}{{ diff }} % vs conseillé
                    } @else {
                      Pour tester le haut du marché
                    }
                  </span>
                  @if (heartStrokePriceM2(); as m2) {
                    <span class="font-bold text-neutral-11">~{{ m2 | number: '1.0-0' : 'fr-FR' }} €/m²</span>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Widget repère marché DVF & Jauge de positionnement dynamique -->
        <etxe-property-dvf-market-reference
          [property]="property()"
          [currentPrice]="model().recommendedPrice ?? sellingPriceEuros()"
          [showGauge]="true"
        />
      </div>

      <!-- 2. HONORAIRES PROPOSÉS (arrive après l'avis de valeur) -->
      <div class="border border-neutral-3 rounded-2xl p-4 sm:p-5 flex flex-col gap-4 bg-neutral-1/50">
        <div class="flex items-center justify-between">
          <span class="text-xs font-extrabold text-neutral-10 uppercase tracking-wider block">
            Honoraires proposés
          </span>
          @if (targetCommissionRate(); as targetRate) {
            <span class="text-[11px] text-indigo-11 font-medium bg-indigo-a2 px-2 py-0.5 rounded-md">
              Cible vendeur : {{ targetRate }} %
            </span>
          }
        </div>

        <!-- ENCADRÉ RAPPEL CANDIDATURE INITIALE & CADRE ±1 POINT -->
        @if (effectiveInitialCommissionRate(); as initRate) {
          <div
            class="rounded-2xl border border-indigo-4/60 bg-indigo-2/30 dark:bg-indigo-3/15 p-4 flex flex-col gap-3"
          >
            <!-- En-tête rappel -->
            <div class="flex items-center justify-between gap-3 flex-wrap">
              <div class="flex items-center gap-2.5">
                <div
                  class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-indigo-9 text-white shadow-xs"
                >
                  <mat-icon class="size-4 !text-base !text-white">history</mat-icon>
                </div>
                <div>
                  <span class="text-xs font-bold text-indigo-12 block">
                    Rappel de votre candidature initiale
                  </span>
                  <span class="text-[11px] text-neutral-11">
                    Base réglementaire fixant votre ajustement d'honoraires
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-2 flex-wrap">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-white dark:bg-neutral-2 border border-neutral-3 text-neutral-12 shadow-2xs"
                >
                  <span class="text-neutral-10 font-normal">Taux initial :</span>
                  <strong class="text-indigo-11">{{ initRate }} %</strong>
                </span>
              </div>
            </div>

            <!-- Règle de tolérance ±1 point & Fourchette autorisée -->
            <div
              class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pt-2 border-t border-indigo-4/40 text-xs"
            >
              <div class="flex items-center gap-1.5 text-neutral-11">
                <mat-icon class="!text-sm size-4 text-indigo-10 shrink-0">rule</mat-icon>
                <span> Fourchette autorisée (<strong>±1 point max</strong>) : </span>
                <span
                  class="font-extrabold text-neutral-12 bg-white dark:bg-neutral-2 px-2 py-0.5 rounded-md border border-neutral-3"
                >
                  {{ minAllowedCommissionRate() }} % à {{ maxAllowedCommissionRate() }} %
                </span>
              </div>

              <!-- Indicateur dynamique en temps réel -->
              @if (commissionRateDelta(); as delta) {
                <div class="flex items-center gap-1.5 self-start sm:self-auto">
                  @if (isCommissionRateOutOfRange()) {
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-bold text-xs bg-red-3 text-red-12 border border-red-5"
                    >
                      <mat-icon class="!text-xs size-3.5 text-red-10">error_outline</mat-icon>
                      <span>Hors limite</span>
                    </span>
                  } @else if (delta === 0) {
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium text-xs bg-neutral-3 text-neutral-12"
                    >
                      <span>Inchangé (0 pt)</span>
                    </span>
                  } @else if (delta > 0) {
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold text-xs bg-amber-3 text-amber-12 border border-amber-5"
                    >
                      <span>+{{ delta }} pt par rapport à l'offre</span>
                    </span>
                  } @else {
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold text-xs bg-jade-3 text-jade-12 border border-jade-5"
                    >
                      <span>{{ delta }} pt par rapport à l'offre</span>
                    </span>
                  }
                </div>
              }
            </div>
          </div>
        } @else {
          <div
            class="flex items-start gap-3 p-3.5 rounded-2xl border border-indigo-4/60 bg-indigo-2/40 text-neutral-12 text-xs leading-relaxed"
          >
            <div
              class="w-7 h-7 rounded-xl bg-indigo-9 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-xs"
            >
              <mat-icon class="text-sm! w-4! h-4! !text-white">info</mat-icon>
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="font-bold text-indigo-12"> Ajustement définitif d'honoraires </span>
              <span class="text-neutral-11">
                Fixez votre taux d'honoraires définitif calculé sur votre prix de vente conseillé (ajustable
                de
                <strong>±1 point</strong> par rapport à votre offre initiale).
              </span>
            </div>
          </div>
        }

        <!-- GRILLE DE SAISIE VALORISÉE : TAUX & MONTANT EN EUROS -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3.5 items-stretch">
          <!-- 1. Carte de saisie du taux (%) -->
          <div
            class="p-4 rounded-2xl bg-white dark:bg-neutral-2 border border-neutral-3 flex flex-col justify-between gap-3 shadow-2xs"
          >
            <div class="flex items-center justify-between">
              <label for="application-commissionRate" class="text-xs font-bold text-neutral-12">
                Taux d'honoraires proposé
              </label>
              @if (minAllowedCommissionRate(); as minR) {
                <span class="text-[11px] text-neutral-10 font-medium">
                  {{ minR }} % à {{ maxAllowedCommissionRate() }} %
                </span>
              }
            </div>

            <!-- Stepper et Input valorisé -->
            <div class="flex items-center gap-2">
              <button
                type="button"
                mat-stroked-button
                (click)="adjustCommissionRate(-0.1)"
                [disabled]="isAdjustDisabled(-0.1)"
                class="!min-w-10 !size-10 !p-0 shrink-0 !border-neutral-3 hover:!bg-neutral-2"
                matTooltip="Diminuer de 0.1 %"
              >
                <mat-icon class="!text-lg size-4 text-neutral-11">remove</mat-icon>
              </button>

              <div class="relative flex-1 flex items-center">
                <input
                  id="application-commissionRate"
                  type="number"
                  step="0.1"
                  [formField]="formTree.commissionRate"
                  class="w-full text-center text-xl sm:text-2xl font-extrabold text-neutral-12 rounded-xl border border-neutral-3 bg-neutral-1 dark:bg-neutral-3/50 py-2 px-3 pr-8 focus:border-primary focus:outline-none transition-colors"
                  [class.!border-red-7]="isCommissionRateOutOfRange()"
                  [class.!bg-red-1/40]="isCommissionRateOutOfRange()"
                />
                <span class="absolute right-3.5 text-sm font-bold text-neutral-10 pointer-events-none"
                  >%</span
                >
              </div>

              <button
                type="button"
                mat-stroked-button
                (click)="adjustCommissionRate(0.1)"
                [disabled]="isAdjustDisabled(0.1)"
                class="!min-w-10 !size-10 !p-0 shrink-0 !border-neutral-3 hover:!bg-neutral-2"
                matTooltip="Augmenter de 0.1 %"
              >
                <mat-icon class="!text-lg size-4 text-neutral-11">add</mat-icon>
              </button>
            </div>

            <div class="text-[11px] text-neutral-10 flex items-center justify-between">
              <span>Ajustement fin (pas de 0.1 %)</span>
              @if (effectiveInitialCommissionRate(); as initRate) {
                <span class="italic">Réf. initiale : {{ initRate }} %</span>
              }
            </div>
          </div>

          <!-- 2. Carte d'impact financier : Montant total en euros -->
          <div
            class="p-4 rounded-2xl bg-indigo-2/40 dark:bg-indigo-3/20 border border-indigo-4/60 flex flex-col justify-between gap-3 shadow-2xs"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-indigo-12 flex items-center gap-1.5">
                <mat-icon class="!text-sm size-3.5 text-indigo-10">payments</mat-icon>
                <span>Honoraires totaux estimés</span>
              </span>
              <span
                class="text-[10px] font-bold text-indigo-11 bg-white/80 dark:bg-neutral-2/80 px-2 py-0.5 rounded-full border border-indigo-4/50"
              >
                TTC
              </span>
            </div>

            <!-- Grand montant en euros & Différence -->
            <div class="flex items-baseline gap-2.5 flex-wrap">
              @if (estimatedCommissionEuros(); as comm) {
                <span class="text-2xl sm:text-3xl font-extrabold text-indigo-12 tracking-tight">
                  {{ comm | number: '1.0-0' : 'fr-FR' }} €
                </span>
              } @else {
                <span class="text-2xl font-bold text-neutral-10">-</span>
              }

              @if (commissionEurosDelta(); as deltaEuros) {
                @if (deltaEuros !== 0) {
                  <span
                    class="text-xs font-bold px-2 py-0.5 rounded-lg border shadow-2xs"
                    [class.bg-amber-3]="deltaEuros > 0"
                    [class.text-amber-12]="deltaEuros > 0"
                    [class.border-amber-5]="deltaEuros > 0"
                    [class.bg-jade-3]="deltaEuros < 0"
                    [class.text-jade-12]="deltaEuros < 0"
                    [class.border-jade-5]="deltaEuros < 0"
                  >
                    {{ deltaEuros > 0 ? '+' : '' }}{{ deltaEuros | number: '1.0-0' : 'fr-FR' }} €
                  </span>
                }
              }
            </div>

            <!-- Référence prix de calcul -->
            <div class="text-[11px] text-neutral-11 flex flex-col gap-0.5 border-t border-indigo-4/40 pt-2">
              @if (effectiveRecommendedPrice(); as price) {
                <span>
                  Calculé sur votre prix conseillé de
                  <strong class="text-neutral-12">{{ price | number: '1.0-0' : 'fr-FR' }} €</strong>
                </span>
              }
              @if (totalFaiEuros(); as fai) {
                <span class="text-neutral-10">
                  Prix total FAI acquéreur :
                  <strong class="text-neutral-12">{{ fai | number: '1.0-0' : 'fr-FR' }} €</strong>
                </span>
              }
            </div>
          </div>
        </div>

        <!-- Message d'alerte si hors limite -->
        @if (isCommissionRateOutOfRange()) {
          <div
            class="flex items-center gap-1.5 p-3 rounded-xl bg-red-2 text-red-12 border border-red-5 text-xs"
          >
            <mat-icon class="!text-sm size-4 text-red-10 shrink-0">error</mat-icon>
            <span>
              Le taux doit être compris entre {{ minAllowedCommissionRate() }} % et
              {{ maxAllowedCommissionRate() }} % (limite légale de ±1 point par rapport à votre offre initiale
              de {{ effectiveInitialCommissionRate() }} %).
            </span>
          </div>
        }
      </div>

      <!-- 3. RÉSUMÉ FINANCIER DE LA PROPOSITION -->
      <div class="border border-neutral-3 rounded-2xl p-4 flex flex-col gap-3 bg-neutral-1/50">
        <div class="flex items-center justify-between">
          <span class="text-xs font-extrabold text-neutral-10 uppercase tracking-wider block">
            Résumé financier de la proposition
          </span>
          @if (pricePerSquareMeter(); as ppm) {
            <span class="text-[11px] font-bold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
              ~{{ ppm | number: '1.0-0' : 'fr-FR' }} €/m² HAI
            </span>
          }
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <!-- Prix HAI -->
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-white dark:bg-neutral-2 border border-neutral-3">
            <span class="text-[10px] font-bold text-neutral-10 uppercase tracking-wider">
              Prix HAI (acquéreur)
            </span>
            <span class="text-base font-extrabold text-neutral-12">
              @if (totalFaiEuros(); as fai) {
                {{ fai | number: '1.0-0' : 'fr-FR' }} €
              } @else {
                -
              }
            </span>
            <span class="text-[10px] text-indigo-11 font-medium">
              @if (estimatedCommissionEuros(); as comm) {
                Dont ~{{ comm | number: '1.0-0' : 'fr-FR' }} € d'honoraires ({{ model().commissionRate }} %)
              }
            </span>
          </div>

          <!-- Net vendeur -->
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-white dark:bg-neutral-2 border border-neutral-3">
            <span class="text-[10px] font-bold text-neutral-10 uppercase tracking-wider"> Net vendeur </span>
            <span class="text-base font-extrabold text-emerald-11">
              @if (netVendeurEuros(); as nv) {
                {{ nv | number: '1.0-0' : 'fr-FR' }} €
              } @else {
                -
              }
            </span>
            <span class="text-[10px] text-neutral-10">Basé sur le prix de vente conseillé</span>
          </div>

          <!-- Surface habitable -->
          <div class="flex flex-col gap-1 p-3 rounded-xl bg-white dark:bg-neutral-2 border border-neutral-3">
            <span class="text-[10px] font-bold text-neutral-10 uppercase tracking-wider">
              Surface habitable
            </span>
            <span class="text-base font-extrabold text-neutral-12">
              @if (surfaceHabitable(); as surf) {
                {{ surf }} m²
              } @else {
                <span class="text-xs text-neutral-10 font-normal">Non renseignée</span>
              }
            </span>
            <span class="text-[10px] text-neutral-10">Donnée certifiée du bien</span>
          </div>
        </div>
      </div>
    }

    <!-- ======================================================== -->
    <!-- CAS 2 : ÉTAPE 1 (CANDIDATURE INITIALE AVANT VISITE)       -->
    <!-- ======================================================== -->
    @else {
      <!-- 1. Proposition d'honoraires & mandat -->
      <div class="border border-neutral-3 rounded-2xl p-4 flex flex-col gap-4 bg-neutral-1/50">
        <div class="flex items-center justify-between">
          <span class="text-xs font-extrabold text-neutral-10 uppercase tracking-wider block">
            Honoraires proposés
          </span>
          @if (targetCommissionRate(); as targetRate) {
            <span class="text-[11px] text-indigo-11 font-medium bg-indigo-a2 px-2 py-0.5 rounded-md">
              Cible vendeur : {{ targetRate }} %
            </span>
          }
        </div>

        <div
          class="flex items-start gap-3 p-3.5 rounded-2xl border border-indigo-4/60 bg-indigo-2/40 text-neutral-12 text-xs leading-relaxed"
        >
          <div
            class="w-7 h-7 rounded-xl bg-indigo-9 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-xs"
          >
            <mat-icon class="text-sm! w-4! h-4! !text-white">info</mat-icon>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="font-bold text-indigo-12"> Ajustement garanti après visite </span>
            <span class="text-neutral-11">
              Votre estimation d'honoraires est indicative. Vous aurez la possibilité de l'ajuster de
              <strong>±1 point</strong> suite à votre première visite du bien, lorsque vous aurez tous les
              éléments techniques en main.
            </span>
          </div>
        </div>

        <!-- Champ taux d'honoraires -->
        <div class="flex flex-col gap-1.5">
          <label for="application-commissionRate" class="text-xs font-semibold text-neutral-11">
            Taux d'honoraires proposé (%)
          </label>
          <div class="relative flex items-center">
            <input
              id="application-commissionRate"
              type="number"
              step="0.1"
              [formField]="formTree.commissionRate"
              class="w-full rounded-xl border border-neutral-3 bg-white dark:bg-neutral-2 p-2.5 pr-8 text-sm font-bold text-neutral-12 focus:border-primary focus:outline-none disabled:opacity-60"
            />
            <span class="absolute right-3 text-xs font-bold text-neutral-9">%</span>
          </div>
        </div>

        <!-- Simulation financière indicative en Étape 1 -->
        @if (sellingPriceEuros(); as netPrice) {
          <div
            class="grid grid-cols-2 gap-2 mt-1 p-2.5 rounded-xl bg-neutral-2/60 border border-neutral-3/60 text-xs"
          >
            <div>
              <span class="text-neutral-10 block text-[10px]">Honoraires estimés</span>
              <span class="font-bold text-indigo-11">
                @if (estimatedCommissionEuros(); as comm) {
                  ~{{ comm | number: '1.0-0' : 'fr-FR' }} €
                } @else {
                  -
                }
              </span>
            </div>
            <div>
              <span class="text-neutral-10 block text-[10px]">Prix total FAI acquéreur</span>
              <span class="font-bold text-neutral-12">
                @if (totalFaiEuros(); as fai) {
                  {{ fai | number: '1.0-0' : 'fr-FR' }} €
                } @else {
                  -
                }
              </span>
            </div>
          </div>
        }
      </div>
    }

    <!-- 3. Prestations de valorisation (réservé à l'offre finale post-visite) -->
    @if (isFinalOfferAdjustment()) {
      <etxe-property-application-services-selector
        [services]="model().services"
        (servicesChange)="onServicesChange($event)"
        title="Prestations et valorisation incluses"
        subtitle="Sélectionnez les prestations adaptées au logement suite à votre visite (incluses dans vos honoraires)"
      />
    }

    <!-- 4. Analyse du bien (Étape 3) ou Message au propriétaire (Étape 1) -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between">
        <label for="application-message" class="text-xs font-bold text-neutral-11 uppercase tracking-wider">
          {{ isFinalOfferAdjustment() ? 'Analyse du bien' : 'Message au propriétaire' }}
        </label>
        <span class="text-[11px] text-neutral-9">
          {{
            isFinalOfferAdjustment()
              ? "Points forts, points d'attention et stratégie"
              : 'Présentation et prise de contact'
          }}
        </span>
      </div>

      @if (hasContactInMessage() && !hasConfirmedWarning()) {
        <aura-alert
          [alertInput]="{
            type: auraColorType.WARN,
            title: 'Partage de coordonnées interdit',
            message:
              'Conformément aux CGU, il est interdit de partager des coordonnées directes avant acceptation du mandat.',
          }"
        >
          <div class="mt-1">
            <button
              type="button"
              (click)="confirmSendAnyway()"
              class="font-bold underline text-amber-12 hover:opacity-80 cursor-pointer text-xs"
            >
              Envoyer quand même
            </button>
          </div>
        </aura-alert>
      }

      <div class="relative">
        <textarea
          id="application-message"
          [formField]="formTree.message"
          rows="5"
          class="w-full rounded-xl border border-neutral-3 bg-neutral-1 p-3 pb-7 text-sm text-neutral-12 placeholder-neutral-8 focus:border-primary focus:outline-none transition-colors resize-none disabled:opacity-60"
          [class.!border-amber-6]="hasContactInMessage() && !hasConfirmedWarning()"
          [placeholder]="
            isFinalOfferAdjustment()
              ? 'Suite à notre visite du bien, voici mon analyse détaillée : les volumes et la luminosité sont d\'excellents atouts... En revanche, le classement DPE nécessitera de cibler des acquéreurs ayant une capacité de financement adaptée...'
              : 'Bonjour, votre bien correspond aux critères de plusieurs acquéreurs qualifiés de mon portefeuille. Je serais ravi d\'échanger avec vous et d\'effectuer une première visite d\'estimation...'
          "
        ></textarea>

        <div
          class="absolute bottom-2 right-3 flex items-center gap-1 text-[11px] pointer-events-none select-none"
        >
          @if (isFinalOfferAdjustment()) {
            @if (messageLength() < 200) {
              <span class="text-amber-11 font-medium">{{ messageLength() }} / 200 min</span>
            } @else {
              <span class="text-emerald-11 font-medium">{{ messageLength() }} / 2000</span>
            }
          } @else {
            <span class="text-neutral-9">{{ messageLength() }} / 2000</span>
          }
        </div>
      </div>

      @if (hasContactInMessage() && !hasConfirmedWarning()) {
        <span class="text-amber-11 text-xs font-medium flex items-center gap-1">
          ⚠️ Coordonnées détectées : elles seront automatiquement masquées.
        </span>
      }
      @if (formTree.message().touched() && formTree.message().invalid()) {
        <span class="text-red-9 text-xs">
          @if (isFinalOfferAdjustment()) {
            @if (messageLength() === 0) {
              L'analyse du bien est requise (min. 200 caractères)
            } @else if (messageLength() < 200) {
              L'analyse du bien doit comporter au moins 200 caractères (encore
              {{ 200 - messageLength() }} caractère{{ 200 - messageLength() > 1 ? 's' : '' }})
            } @else if (messageLength() > 2000) {
              L'analyse du bien ne doit pas dépasser 2000 caractères
            }
          } @else {
            Le message est requis (min. 10 caractères, max. 2000)
          }
        </span>
      }
    </div>
  </fieldset>

  <!-- Form Actions -->
  <div class="flex gap-2 mt-1">
    <button
      type="button"
      mat-button
      class="flex-1 !py-3 border border-neutral-3 text-neutral-11 hover:bg-neutral-2 text-sm transition-all"
      (click)="onCancel()"
      [disabled]="isLoading()"
    >
      Annuler
    </button>
    <button
      type="submit"
      mat-flat-button
      class="flex-2 !py-3 bg-primary hover:bg-primary-hover text-white text-sm font-semibold shadow-md shadow-primary/20 transition-all primary flex items-center justify-center gap-2 disabled:!bg-neutral-3 dark:disabled:!bg-neutral-3 disabled:!text-neutral-8 dark:disabled:!text-neutral-7 disabled:!shadow-none disabled:cursor-not-allowed disabled:hover:!bg-neutral-3"
      [disabled]="isSubmitDisabled()"
    >
      @if (isLoading()) {
        <mat-spinner diameter="18" class="inline-block"></mat-spinner>
      }
      <span>{{
        isFinalOfferAdjustment() ? 'Valider mon avis de valeur et offre finale' : 'Envoyer ma candidature'
      }}</span>
    </button>
  </div>
</form>
